import { makeStyles } from "@mui/styles";
import { Box, Card, Link, Stack } from "@mui/material";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { UserContext } from "../../../context/UserContext";
import { CourseStructureCtx, UserCtx } from "../../../interfaces";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ModuleListCard } from "./ModuleListCard";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as IconFilter } from "../../../assets/Icon-Filter.svg";
import { Modal } from "@mui/material";
import { ModuleFilterModal } from "./ModuleFIlterModal";
import { CustomChip } from "../utils/CustomChip";
import { generateGtmEvent } from "../../../Helpers";
import { Module as ModuleTree, Question } from "../../utils/CourseTree";

const useStyle = makeStyles((theme) => ({
  root: {},
  filterButton: {
    backgroundColor: "transparent",
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    width: "93px",
    height: "40px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      width: "93px",
      height: "40px",
      color: theme.palette.text.primary,
      transform: "none",
    },
  },
  filterButtonActive: {
    backgroundColor: "#F2F2F2",
    width: "93px",
    height: "40px",
    color: theme.palette.text.primary,
    transform: "none",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      width: "93px",
      height: "40px",
      color: theme.palette.text.primary,
      transform: "none",
    },
  },
  filterModal: {
    position: "absolute",
    width: "500px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  filterButtonIcon: {
    width: "16px",
    height: "16px",
  },
  filterButtonTextIcon: {
    width: "18px",
    height: "18px",
  },
  showMoreButton: {
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 700,
    height: "48px",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
  },
}));

export const ModuleListContainer = () => {
  const classes = useStyle();
  const availableFilters = [
    {
      id: "completed",
      label: "Completed",
    },
    {
      id: "inProgress",
      label: "In progress",
    },
    {
      id: "notStarted",
      label: "Not started",
    },
  ];
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const metadata = courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const useDigitStudyTool = metadata?.use_digit_study_tool ?? false;
  const userCtx = useContext(UserContext) as UserCtx;

  const [openFilter, setFilterOpen] = useState(false);

  const [lastUnlockedIndex, setLastUnlockedIndex] = useState<number>(0);
  const [showGradient, setShowGradient] = useState<boolean>(true);
  const [moduleListExpanded, setModuleListExpanded] = useState<boolean>(false);

  const HandleDataLayer = (event: string, action: string, type: string, text: string) => {
    generateGtmEvent({
      event: event,
      action: action,
      name: "onclick",
      type: type,
      region: "main content",
      section: "all modules",
      text: text,
    });
  };
  const handleOpen = () => {
    HandleDataLayer("modal", "open", "click", "filter");
    setFilterOpen(true);
  };
  const handleClose = () => setFilterOpen(false);

  const [filters, setFilters] = useState<string[]>([]);

  const moduleRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [totalHeight, setTotalHeight] = useState(0);
  const [hiddenHeight, setHiddenHeight] = useState(0);

  const filterBoxRef = useRef<HTMLDivElement | null>(null);
  const [filterBoxHeight, setFilterBoxHeight] = useState(0);

  useEffect(() => {
    if (filterBoxRef.current) {
      setFilterBoxHeight(filterBoxRef.current.offsetHeight);
    }
  }, [filters]);

  const filteredModules = useMemo(() => {
    if (!courseStructureCtx.courseTreeStructure) return [];
    let modules = courseStructureCtx.courseTreeStructure
      .getAllModulesInCourse()
      .filter((module: ModuleTree) =>
        userCtx.checkFeatureFlagAccess(module.feature_flag_id)
      )
      .filter((module: ModuleTree) => {
        if (filters.length === 0) return true;
        if (filters.includes("completed") && module.isCompleted()) return true;
        if (filters.includes("inProgress") && module.isInProgress()) return true;
        return (
          filters.includes("notStarted") &&
          !module.isCompleted() &&
          !module.isInProgress()
        );
      });
    return modules ?? [];
  }, [filters, courseStructureCtx.courseTreeStructure, userCtx]);

  /* This useEffect handles a specific UI requirement:
   All unlocked modules should be visible by default, regardless of any locked modules appearing in between.
   Additionally, when a module is expanded or collapsed, the container height should adjust dynamically to ensure
   visibility up to the last unlocked module. */
  useEffect(() => {
    //This condition is to set showGradient when the filters are cleared
    if (filters.length > 0) {
      setShowGradient(false);
      setModuleListExpanded(true);
      return;
    } else {
      setShowGradient(true);
    }

    if (!useDigitStudyTool) {
      setShowGradient(false);
      setModuleListExpanded(true);
      return;
    }
    if (!moduleRefs.current.length) return;

    for (let i = filteredModules.length - 1; i >= 0; i--) {
      if (filteredModules[i].is_asset_locked === false) {
        setLastUnlockedIndex(i);
        break;
      }
    }

    // If the last module is unlocked or if there less than 1 module(after filter),
    // there's no need to collapse the list or show the gradient overlay.
    if (lastUnlockedIndex === filteredModules.length - 1 || filteredModules.length <= 1) {
      setShowGradient(false);
      setModuleListExpanded(true);
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      const newTotalHeight = moduleRefs.current.reduce((sum, el) => {
        return sum + (el?.offsetHeight || 0);
      }, 0);

      // Calculate the total margin between modules (24px gap between each)
      const totalMargin = (filteredModules.length - 1) * 24;
      const totalHeight = newTotalHeight + totalMargin;
      setTotalHeight(totalHeight);

      // Calculate hidden height from (lastUnlockedIndex + 1) to the end
      const hiddenModuleEls = moduleRefs.current.slice(lastUnlockedIndex + 1);
      const hiddenHeightOnly = hiddenModuleEls.reduce((sum, el) => {
        return sum + (el?.offsetHeight || 0);
      }, 0);

      const hiddenMargin = (lastUnlockedIndex + 1) * 24;
      setHiddenHeight(hiddenHeightOnly - hiddenMargin);
    });

    moduleRefs.current.forEach((el) => {
      if (el) resizeObserver.observe(el);
    });

    return () => resizeObserver.disconnect();
  }, [filters.length, filteredModules, lastUnlockedIndex, useDigitStudyTool]);

  const handleFilter = (filter: string) => {
    if (filters.includes(filter)) {
      setFilters(filters.filter((f) => f !== filter));
    } else {
      setFilters([...filters, filter]);
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "48px",
          display: "flex",
          marginTop: "54px",
          marginBottom: "2rem",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Typography
          sx={{ fontSize: "28px", letterSpacing: "0.28px", color: "#191919" }}
          variant="h2">
          All modules
        </Typography>
        <Button
          onClick={handleOpen}
          size="medium"
          className={
            filters.length > 0 ? classes.filterButtonActive : classes.filterButton
          }
          variant="contained"
          endIcon={
            filters.length > 0 ? (
              <Box
                sx={{ borderRadius: "50%", backgroundColor: "#000000" }}
                className={classes.filterButtonTextIcon}>
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontSize: "0.6em",
                    fontWeight: "600",
                  }}>
                  {filters.length}
                </Typography>
              </Box>
            ) : (
              <IconFilter className={classes.filterButtonIcon} />
            )
          }>
          Filter
        </Button>
      </Box>
      {filters.length > 0 ? (
        <Box
          ref={filterBoxRef}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            marginBottom: "24px",
            alignItems: "center",
          }}>
          {filters.map((filter) => {
            const filterObj = availableFilters.find((f) => f.id === filter);
            return (
              <CustomChip
                key={`filter-${filter}`}
                label={filterObj?.label}
                onClose={() => {
                  HandleDataLayer(
                    "select",
                    "unclick",
                    "checkbox option",
                    `${filterObj?.label}`
                  );
                  handleFilter(filter);
                }}
              />
            );
          })}
          <Link
            onClick={() => {
              HandleDataLayer("select", "unclick", "checkbox option", "clear all");
              setFilters([]);
            }}
            href="#"
            variant="body1">
            Clear all
          </Link>
        </Box>
      ) : null}
      <Box>
        <Box
          sx={{
            position: "relative",
            ...(moduleListExpanded
              ? {
                  maxHeight: "none",
                  overflow: "visible",
                }
              : {
                  maxHeight: totalHeight - hiddenHeight + filterBoxHeight,
                  overflow: "hidden",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "60px",
                    background:
                      "linear-gradient(to bottom, rgba(250, 250, 250, 0.25), rgba(250, 250, 250, 0.50), rgba(250, 250, 250, 0.75), rgba(250, 250, 250, 0.85), #FAFAFA)",
                  },
                }),
          }}>
          {filteredModules.length > 0 ? (
            filteredModules.map((module, index) => {
              const resumeAtQuestion = userCtx.getProblemToResumeOnModule(
                module.id,
                true
              );
              const moduleProgress = module.getProgress() ?? 0;
              const currentModuleName = module.getFormattedModuleName();

              // this is logic for the Target/deeplink lockdown in LTI mode
              const targetsInCourse =
                courseStructureCtx.courseTreeStructure?.getAllTargetsInCourse();
              const questionsInTargets = targetsInCourse?.flatMap(
                (item) => item.children
              ) as Question[] | undefined;

              const firstTarget =
                targetsInCourse && targetsInCourse.length > 0
                  ? targetsInCourse[0]
                  : undefined;
              const appendTarget = targetsInCourse && targetsInCourse.length === 1;

              const isDemoLocked = !metadata?.demo_overrides?.course_content?.modules
                ? index > 0
                : !metadata.demo_overrides.course_content.modules.includes(module.id);

              const isDemoClass = userCtx.studentCurrentClass?.is_demo;

              const moduleTabIndex =
                module.is_asset_locked || index > lastUnlockedIndex ? -1 : 0;

              const dropDownTabIndex =
                moduleListExpanded || index <= lastUnlockedIndex ? 0 : -1;

              const activeModule =
                module.id === userCtx.currentLtiAssignment?.digit_resource_id;
              // have to check against the questions in the targets as deeplinked target level links to question ID
              const activeTarget = questionsInTargets?.find(
                (item) => item.id === userCtx.currentLtiAssignment?.digit_resource_id
              );

              if (resumeAtQuestion && resumeAtQuestion.id) {
                return (
                  <ModuleListCard
                    key={module.id}
                    ref={(el) => (moduleRefs.current[index] = el)}
                    isDemoLocked={isDemoClass && isDemoLocked}
                    index={index}
                    moduleId={module.id}
                    isModuleLocked={module.is_asset_locked}
                    moduleName={
                      appendTarget
                        ? `${currentModuleName}: ${firstTarget?.getFormattedTargetName()}`
                        : currentModuleName
                    }
                    moduleOrder={module.order}
                    showModuleNumber={
                      !metadata?.module_overview_page?.disable_module_number
                    }
                    percentComplete={moduleProgress}
                    resumeAtQuestionId={resumeAtQuestion.id}
                    moduleTabIndex={moduleTabIndex}
                    dropdownTabIndex={dropDownTabIndex}
                    isActive={activeModule || Boolean(activeTarget)}
                  />
                );
              } else {
                return <></>;
              }
            })
          ) : (
            <Box>
              <Typography variant="h4">No results</Typography>
              <Typography variant="body2" sx={{ marginTop: "16px" }}>
                It looks like your filters produced no results. Clear filters to display
                all modules or try a new filter.
              </Typography>
            </Box>
          )}
        </Box>
        {filteredModules.length > 0 && showGradient === true && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ marginTop: "32px", marginBottom: "8px" }}>
            <Button
              className={classes.showMoreButton}
              onClick={() => {
                setModuleListExpanded(!moduleListExpanded);
              }}>
              <i
                className={`fa-regular ${
                  moduleListExpanded ? "fa-chevron-up" : "fa-chevron-down"
                }`}></i>
              <Typography
                variant="h4"
                component="span"
                sx={{ marginLeft: "8px" }}
                fontSize="inherit"
                fontWeight="inherit"
                color="inherit">
                {moduleListExpanded ? "Show less" : "Show all modules"}
              </Typography>
            </Button>
          </Stack>
        )}
      </Box>
      <Modal open={openFilter} onClose={handleClose} aria-labelledby="filter-modal-title">
        <Card className={classes.filterModal}>
          <ModuleFilterModal
            close={handleClose}
            filters={filters}
            setFilters={setFilters}
            availableFilters={availableFilters}
          />
        </Card>
      </Modal>
    </>
  );
};
